import { useRef, useState, type ChangeEventHandler, type DragEventHandler } from "react";
import { FileText, Upload, X } from "lucide-react";
import { Spinner } from "../Spinner";
import { labelClass } from "./fieldStyles";

interface FileUploadProps {
  id: string;
  label: string;
  /** Lowercase extensions without the dot. */
  acceptExtensions: readonly string[];
  file: File | null;
  onFile: (file: File | null) => void;
  isProcessing?: boolean;
  hint?: string;
  error?: string | null;
  required?: boolean;
  children?: never;
}

/** Local file picker with drag and drop. The parent decides what to do with the File. */
export function FileUpload({ id, label, acceptExtensions, file, onFile, isProcessing, hint, error, required }: FileUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const acceptAttr = acceptExtensions.map((extension) => `.${extension}`).join(",");
  const acceptLabel = acceptExtensions.map((extension) => extension.toUpperCase()).join(", ");
  const shownError = localError ?? error ?? null;

  const acceptFile = (candidate: File | undefined) => {
    if (!candidate) return;
    const extension = candidate.name.split(".").pop()?.toLowerCase() ?? "";
    if (!acceptExtensions.includes(extension)) {
      setLocalError(`Accepted file types: ${acceptLabel}`);
      return;
    }
    setLocalError(null);
    onFile(candidate);
  };

  const handleDrop: DragEventHandler<HTMLLabelElement> = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    acceptFile(event.dataTransfer.files[0]);
  };

  const handleInputChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    acceptFile(event.currentTarget.files?.[0]);
    event.currentTarget.value = "";
  };

  if (file) {
    return (
      <div>
        <span className={labelClass()}>{label}</span>
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
          {isProcessing ? <Spinner size="sm" /> : <FileText size={20} className="shrink-0 text-gray-400" aria-hidden="true" />}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-800">{file.name}</p>
            <p className="text-xs text-gray-500">{isProcessing ? "Reading file..." : `${(file.size / 1024).toFixed(1)} KB`}</p>
          </div>
          <button type="button" onClick={() => onFile(null)} disabled={isProcessing} aria-label={`Remove ${file.name}`} title="Remove file" className="flex min-h-[44px] min-w-[44px] items-center justify-center text-gray-400 hover:text-danger disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-brand-green-50 rounded-lg">
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {hint && <p className="mt-1.5 text-xs text-gray-500">{hint}</p>}
      </div>
    );
  }

  return (
    <div>
      <span className={labelClass()}>
        {label}
        {required && (
          <span className="text-danger" aria-hidden="true">
            &nbsp;*
          </span>
        )}
      </span>
      <label
        htmlFor={id}
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-6 transition-colors focus-within:ring-2 focus-within:ring-brand-green-50 ${
          isDragOver ? "border-brand-green bg-brand-green-05" : shownError ? "border-danger bg-danger-bg" : "border-gray-300 hover:border-brand-green hover:bg-brand-green-05"
        }`}
      >
        <input ref={inputRef} id={id} type="file" accept={acceptAttr} onChange={handleInputChange} aria-label={label} aria-required={required || undefined} aria-invalid={shownError ? true : undefined} className="sr-only" />
        <Upload size={24} className="text-gray-400" aria-hidden="true" />
        <span className="text-sm font-medium text-gray-600">Click to upload or drag and drop</span>
        <span className="text-xs text-gray-500">{acceptLabel}</span>
      </label>
      {hint && <p className="mt-1.5 text-xs text-gray-500">{hint}</p>}
      {shownError && (
        <p role="alert" className="mt-1 text-xs text-danger">
          {shownError}
        </p>
      )}
    </div>
  );
}
